import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { EyeOff, Loader2, RefreshCw, Trash2, UserPlus, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = '/api';

export default function HiddenEmailsPage() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState('');

  const load = async () => {
    setData(null);
    try { const r = await axios.get(`${API}/data-ops/hidden-emails`, { withCredentials: true }); setData(r.data); setSelected(new Set()); }
    catch { setData({ items: [], total: 0, error: true }); }
  };
  useEffect(() => { load(); }, []);

  const items = data?.items || [];
  const allSelected = items.length > 0 && selected.size === items.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(items.map(i => i.company_id)));
  const toggle = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const act = async (kind) => {
    if (selected.size === 0) return;
    const verb = kind === 'clear' ? 'remove & suppress' : 'make visible as a contact';
    if (!window.confirm(`${verb.charAt(0).toUpperCase() + verb.slice(1)} ${selected.size} hidden address${selected.size === 1 ? '' : 'es'}?`)) return;
    setBusy(kind);
    try {
      const r = await axios.post(`${API}/data-ops/hidden-emails/${kind}`, { company_ids: [...selected] }, { withCredentials: true });
      toast.success(kind === 'clear' ? `Cleared & suppressed ${r.data.cleared}` : `Promoted ${r.data.promoted} to visible contacts`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
    finally { setBusy(''); }
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="hidden-emails-page">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <EyeOff className="w-6 h-6 text-amber-600" /> Hidden Recipients
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            Biotech profiles whose only email address lives in a legacy field that doesn't show under <b>Contacts</b> — yet outreach falls back to it when the contact list is empty.
            Clear them (the address is suppressed so nothing can be sent to it again) or promote them so they appear on the profile where you can edit or delete them.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="hidden-emails-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
      </div>

      {!data ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Scanning biotech profiles…</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-sm text-emerald-800" data-testid="hidden-emails-empty">
          No hidden addresses — every emailable address is visible on its profile.
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-3" data-testid="hidden-emails-toolbar">
            <span className="text-sm text-slate-600"><b>{data.total}</b> companies · <b>{selected.size}</b> selected</span>
            <div className="flex-1" />
            <Button size="sm" variant="outline" onClick={() => act('promote')} disabled={!selected.size || !!busy} data-testid="hidden-emails-promote">
              {busy === 'promote' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <UserPlus className="w-3.5 h-3.5 mr-1" />} Make visible
            </Button>
            <Button size="sm" onClick={() => act('clear')} disabled={!selected.size || !!busy} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="hidden-emails-clear">
              {busy === 'clear' ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />} Remove &amp; suppress
            </Button>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <table className="w-full text-sm" data-testid="hidden-emails-table">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left"><input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="hidden-emails-select-all" /></th>
                  <th className="px-3 py-2 text-left">Company</th>
                  <th className="px-3 py-2 text-left">Hidden address</th>
                  <th className="px-3 py-2 text-left">Visible contacts</th>
                  <th className="px-3 py-2 text-left">Last outreach</th>
                </tr>
              </thead>
              <tbody>
                {items.map(i => (
                  <tr key={i.company_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`hidden-email-row-${i.company_id}`}>
                    <td className="px-3 py-2"><input type="checkbox" checked={selected.has(i.company_id)} onChange={() => toggle(i.company_id)} /></td>
                    <td className="px-3 py-2">
                      <Link to={`/biotech/${i.company_id}`} className="font-medium text-slate-900 hover:text-sky-700 inline-flex items-center gap-1">
                        {i.company_name} <ExternalLink className="w-3 h-3 text-slate-400" />
                      </Link>
                      <div className="text-[11px] text-slate-400">{i.country}{i.dnc ? ' · DNC' : ''}</div>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-amber-800">{i.email}{i.contact_name ? <span className="font-sans text-slate-500"> · {i.contact_name}</span> : null}</td>
                    <td className="px-3 py-2 text-slate-600">{i.visible_contacts === 0 ? <span className="text-rose-600 font-medium">none</span> : i.visible_contacts}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {i.last_outreach ? `${(i.last_outreach.created_at || '').slice(0, 10)} · ${i.last_outreach.status || ''}` : '—'}
                      {i.last_email_event?.type === 'blocked' && <span className="ml-1 text-emerald-700">(blocked by gate)</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
